// src/pages/TempleExhibitionPage.jsx
import React, { useEffect, useState, useRef } from "react";
import { Helmet } from "react-helmet";
import SiteHeader from "../components/SiteHeader";
import Footer from "../components/Footer";
import { MOCK_TEMPLES } from "../lib/templeMockData";

// React Leaflet + Leaflet (OpenStreetMap)
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

/** ====== custom div icon (ไม่ต้องใช้ไฟล์รูปแยก) ====== */
const templeIcon = L.divIcon({
  className: "",
  html: `
    <div style="
      background:#d8653b;
      width:22px;
      height:22px;
      border-radius:999px;
      display:flex;
      align-items:center;
      justify-content:center;
      color:#fff7ed;
      font-size:11px;
      border:2px solid #fed7aa;
      box-shadow:0 0 0 1px rgba(0,0,0,0.08);
    ">
      🛕
    </div>
  `,
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

/** helper component: เวลาเลือกวัดใหม่ให้ map ค่อย ๆ เลื่อนไปหา */
function SmoothMapFocus({ lat, lng }) {
  const map = useMap();
  React.useEffect(() => {
    if (lat && lng) {
      map.flyTo([lat, lng], 14, { duration: 0.8 });
    }
  }, [lat, lng, map]);
  return null;
}

/** helper: เลือกค่าตามภาษา (ตอนนี้มีแค่ th / en) */
function pickByLang(langCode, thVal, enVal) {
  if (langCode === "en") return enVal || thVal || "";
  return thVal || enVal || "";
}

export default function TempleExhibitionPage() {
  const { t, i18n } = useTranslation();
  const langCode = (i18n.language || "th").split("-")[0]; // "th", "en", "lo", "fil", "zh"...
  const hasData = MOCK_TEMPLES && MOCK_TEMPLES.length > 0;

  const [selectedId, setSelectedId] = useState(
    hasData ? MOCK_TEMPLES[0].id : null
  );
  const [selectedBookIndex, setSelectedBookIndex] = useState(0);
  
  // ใช้ useRef เพื่อเก็บอ้างอิงถึง Marker บนแผนที่
  const markerRefs = useRef({});

  // --- REACT-SNAP Logic ---
  useEffect(() => {
    // 1. สั่งหยุดไว้ก่อน (เผื่อ Map กำลังโหลด)
    if (window.snapSaveState) {
      window.snapSaveState = () => {};
    }

    // 2. ข้อมูลเป็น Static ก็จริง แต่ Map ต้องใช้เวลาวาด Container
    // รอ 500ms ให้ชัวร์ว่า Layout นิ่งแล้วค่อย Save
    const timer = setTimeout(() => {
      if (window.snapSaveState) {
        window.snapSaveState = null;
      }
    }, 500);

    return () => clearTimeout(timer);
  }, []);
  // ------------------------

  // --- Logic สำหรับเปิด Popup อัตโนมัติเมื่อ selectedId เปลี่ยน ---
  useEffect(() => {
    const marker = markerRefs.current[selectedId];
    if (marker) {
      // สั่งเปิด Popup ของ Marker นั้นๆ
      marker.openPopup();
    }
  }, [selectedId]);
  // -----------------------------------------------------------

  const selectedTemple = hasData
    ? MOCK_TEMPLES.find((temple) => temple.id === selectedId) || MOCK_TEMPLES[0]
    : null;

  const books = selectedTemple?.books || [];
  const currentBook = books[selectedBookIndex] || null;

  // reset เล่มหนังสือที่เลือกเวลาผู้ใช้เปลี่ยนวัด / สถานที่
  useEffect(() => {
    setSelectedBookIndex(0);
  }, [selectedId]);

  // center เริ่มต้นของแผนที่ (คูเมืองเชียงใหม่โดยประมาณ)
  const defaultCenter = [18.7904, 98.9853];

  return (
    <div className="min-h-screen bg-[#f8f3ea] flex flex-col">
      {/* ===== Helmet (SEO) ===== */}
      <Helmet>
        <title>
          {t(
            "templeExhibition.title",
            "ตามหนังสือเที่ยวสถานที่สำคัญในเชียงใหม่"
          )}{" "}
          · Digital Library
        </title>
        <meta
          name="description"
          content={t(
            "templeExhibition.subtitle",
            "เริ่มจากหนังสือหายากและเอกสารโบราณในคลัง แล้วออกเดินทางไปยังวัดสำคัญ กำแพงเมือง ประตูเมือง และแหล่งเรียนรู้รอบคูเมือง"
          )}
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content={t(
            "templeExhibition.title",
            "ตามหนังสือเที่ยวสถานที่สำคัญในเชียงใหม่"
          )}
        />
        <meta
          property="og:description"
          content="นิทรรศการเสมือนจริง เชื่อมโยงเรื่องราวจากหน้าหนังสือสู่สถานที่จริงในประวัติศาสตร์เมืองเชียงใหม่"
        />
      </Helmet>

      <SiteHeader />

      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* header ของหน้านิทรรศการ */}
        <header className="mb-6">
          <p className="text-[10px] sm:text-xs uppercase tracking-[0.25em] text-[#c27a4f] mb-2">
            Virtual Exhibition · Chiang Mai
          </p>
          <h1 className="text-2xl sm:text-3xl font-semibold text-[#5b4a3e] mb-2">
            {t(
              "templeExhibition.title",
              "ตามหนังสือเที่ยวสถานที่สำคัญในเชียงใหม่"
            )}
          </h1>
          <p className="text-sm sm:text-base text-[#6b5a4a] max-w-2xl leading-relaxed">
            {t(
              "templeExhibition.subtitle",
              "เริ่มจากหนังสือหายากและเอกสารโบราณในคลัง แล้วออกเดินทางไปยังวัดสำคัญ กำแพงเมือง ประตูเมือง และแหล่งเรียนรู้รอบคูเมือง เลือกจุดบนแผนที่หรือชื่อสถานที่ เพื่ออ่านเรื่องราวที่เชื่อมโยงระหว่าง “หน้าหนังสือ” กับ “หน้าประวัติศาสตร์เมืองเชียงใหม่”."
            )}
          </p>
        </header>

        {!hasData ? (
          <div className="bg-white/80 rounded-2xl shadow-sm p-6 text-sm text-gray-600">
            ยังไม่มีข้อมูล MOCK_TEMPLES กรุณาเติมข้อมูลใน
            <code className="mx-1 px-1 py-0.5 rounded bg-gray-100 text-[11px]">
              src/lib/templeMockData.js
            </code>
          </div>
        ) : (
          <div className="grid lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1.1fr)] gap-6">
            {/* ========== ฝั่งซ้าย: แผนที่ + สรุปสถานที่ที่เลือก ========== */}
            <section className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-sm p-4 flex flex-col gap-3">
              <div className="flex items-center justify-between gap-2 mb-1">
                <h2 className="text-base font-semibold text-[#5b4a3e]">
                  {t(
                    "templeExhibition.mapTitle",
                    "แผนที่สถานที่สำคัญในเชียงใหม่จากหนังสือหายาก (OpenStreetMap)"
                  )}
                </h2>
                <span className="hidden sm:inline text-[11px] text-gray-500">
                  {t("templeExhibition.totalSpots", {
                    defaultValue: "{{count}} จุดในนิทรรศการ",
                    count: MOCK_TEMPLES.length,
                  })}
                </span>
              </div>

              <div className="w-full h-[260px] sm:h-[340px] lg:h-[380px] rounded-2xl overflow-hidden shadow-inner">
                <MapContainer
                  center={defaultCenter}
                  zoom={13}
                  scrollWheelZoom={false}
                  className="w-full h-full"
                >
                  <TileLayer
                    attribution='&copy; OpenStreetMap contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />

                  {/* เลื่อน focus ไปยังสถานที่ที่เลือก */}
                  {selectedTemple && (
                    <SmoothMapFocus
                      lat={selectedTemple.lat}
                      lng={selectedTemple.lng}
                    />
                  )}

                  {MOCK_TEMPLES.map((temple) => {
                    const primaryBook = temple.books?.[0] || null;
                    const bookTitle = primaryBook
                      ? pickByLang(
                          langCode,
                          primaryBook.titleTh,
                          primaryBook.titleEn
                        )
                      : "";

                    return (
                      <Marker
                        key={temple.id}
                        position={[temple.lat, temple.lng]}
                        icon={templeIcon}
                        ref={(ref) => {
                          if (ref) {
                            markerRefs.current[temple.id] = ref;
                          }
                        }}
                        eventHandlers={{
                          click: () => setSelectedId(temple.id),
                        }}
                      >
                        <Popup>
                          <div className="text-xs">
                            <strong>{temple.nameTh}</strong>
                            <br />
                            <span className="text-[11px] text-gray-600">
                              {temple.nameEn}
                            </span>
                            {bookTitle && (
                              <>
                                <br />
                                <span className="text-[11px] text-gray-600">
                                  {bookTitle}
                                </span>
                              </>
                            )}
                            {temple.books && temple.books.length > 1 && (
                              <div className="mt-1 text-[10px] text-gray-500">
                                + {temple.books.length - 1}{" "}
                                {t(
                                  "templeExhibition.moreBooks",
                                  "เล่มที่เกี่ยวข้องในคลังหนังสือ"
                                )}
                              </div>
                            )}
                          </div>
                        </Popup>
                      </Marker>
                    );
                  })}
                </MapContainer>
              </div>

              {/* แถบข้อมูลสถานที่ที่เลือก (อ่านเร็ว ๆ) */}
              {selectedTemple && (
                <div className="mt-1 rounded-xl bg-white/80 px-3 py-2 text-[11px] sm:text-xs text-gray-700 flex flex-col gap-1">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="text-[10px] uppercase tracking-[0.18em] text-[#c27a4f]">
                      {t("templeExhibition.currentSpot", "สถานที่ที่เลือก")}
                    </span>
                    <span className="font-semibold text-[#5b4a3e]">
                      {selectedTemple.nameTh}
                    </span>
                    <span className="text-[11px] text-gray-500">
                      ({selectedTemple.nameEn})
                    </span>
                  </div>
                  <div className="text-[11px] text-gray-500">
                    {selectedTemple.district}
                  </div>
                  <div className="text-[10px] text-gray-500">
                    GPS: {selectedTemple.lat.toFixed(4)},{" "}
                    {selectedTemple.lng.toFixed(4)}
                  </div>
                  {books.length > 0 && (
                    <div className="text-[11px] text-gray-600 mt-1">
                      {t(
                        "templeExhibition.bookCountLabel",
                        "หนังสือหายากในคลังที่เกี่ยวข้อง"
                      )}
                      :{" "}
                      <span className="font-semibold">
                        {books.length}{" "}
                        {t(
                          "templeExhibition.bookUnit",
                          books.length > 1 ? "เล่ม" : "เล่ม"
                        )}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </section>

            {/* ========== ฝั่งขวา: รายชื่อสถานที่ + รายละเอียดหนังสือ ========== */}
            <section className="space-y-4">
              {/* รายชื่อสถานที่ทั้งหมด (เลือกจากรายการ) */}
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-sm p-4">
                <h2 className="text-base font-semibold text-[#5b4a3e] mb-2">
                  {t(
                    "templeExhibition.templeListTitle",
                    "รายการวัด กำแพงเมือง และแหล่งเรียนรู้ในนิทรรศการ"
                  )}{" "}
                  <span className="text-xs text-gray-500">
                    ({MOCK_TEMPLES.length})
                  </span>
                </h2>

                <div className="max-h-64 overflow-y-auto pr-1 space-y-1">
                  {MOCK_TEMPLES.map((temple) => {
                    const countBooks = temple.books?.length || 0;
                    return (
                      <button
                        key={temple.id}
                        type="button"
                        onClick={() => setSelectedId(temple.id)}
                        className={`w-full text-left px-3 py-2 rounded-xl text-xs sm:text-sm transition
                          ${
                            temple.id === selectedId
                              ? "bg-[#ffedd5] text-[#5b4a3e] font-semibold shadow-inner"
                              : "bg-white hover:bg-[#fdf3e6] text-[#5b4a3e]"
                          }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex-1">
                            <div>{temple.nameTh}</div>
                            <div className="text-[11px] text-gray-500">
                              {temple.nameEn}
                            </div>
                            <div className="text-[10px] text-gray-400">
                              {temple.district}
                            </div>
                          </div>
                          {countBooks > 0 && (
                            <div className="flex flex-col items-end text-[10px] text-[#c27a4f]">
                              <span className="font-medium">
                                {countBooks}{" "}
                                {t(
                                  "templeExhibition.bookUnitShort",
                                  "เล่ม"
                                )}
                              </span>
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* ถ้ามีหนังสือ: แสดงรายการเล่ม + เล่มที่เลือกละเอียด */}
              {selectedTemple && books.length > 0 && (
                <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-sm p-5 space-y-3">
                  {/* หัวข้อกล่อง: ชื่อสถานที่ + คำอธิบายสั้น */}
                  <div className="flex flex-col sm:flex-row gap-3">
                    {(() => {
                      const coverSrc =
                        currentBook?.coverImage ||
                        selectedTemple.thumbnail;
                      return (
                        coverSrc && (
                          <img
                            src={coverSrc}
                            alt={selectedTemple.nameTh}
                            className="w-24 h-24 sm:w-28 sm:h-28 rounded-xl object-cover flex-shrink-0"
                          />
                        )
                      );
                    })()}

                    <div className="flex-1 space-y-1">
                      <h3 className="text-sm sm:text-base font-semibold text-[#5b4a3e]">
                        {selectedTemple.nameTh}
                      </h3>
                      <p className="text-[11px] sm:text-xs text-gray-500">
                        {selectedTemple.nameEn}
                      </p>
                      <p className="text-[11px] sm:text-xs text-gray-500">
                        {selectedTemple.district}
                      </p>
                      <p className="text-[11px] sm:text-xs text-gray-600 mt-1">
                        {t(
                          "templeExhibition.relatedBooksHeading",
                          "หนังสือหายากจากหอสมุดที่เชื่อมโยงกับสถานที่นี้"
                        )}
                      </p>
                    </div>
                  </div>

                  {/* รายชื่อหนังสือแบบปุ่มเลือกเล่ม */}
                  <div className="flex flex-wrap gap-2 mt-1">
                    {books.map((b, idx) => {
                      const labelTitle = pickByLang(
                        langCode,
                        b.titleTh,
                        b.titleEn
                      );
                      return (
                        <button
                          key={b.id || idx}
                          type="button"
                          onClick={() => setSelectedBookIndex(idx)}
                          className={`text-[11px] px-3 py-1.5 rounded-full border transition
                            ${
                              idx === selectedBookIndex
                                ? "bg-[#d8653b] text-white border-[#d8653b] shadow"
                                : "bg-white text-[#5b4a3e] border-[#f97316]/40 hover:bg-[#fff7ed]"
                            }`}
                        >
                          {labelTitle}
                        </button>
                      );
                    })}
                  </div>

                  {/* การ์ดรายละเอียดเล่มที่เลือก */}
                  {currentBook && (
                    <article className="mt-2 border border-[#fed7aa] rounded-2xl bg-[#fff7ed]/80 px-4 py-3 text-xs sm:text-sm text-gray-800 space-y-1.5">
                      <h4 className="text-sm sm:text-base font-semibold text-[#5b4a3e]">
                        {pickByLang(
                          langCode,
                          currentBook.titleTh,
                          currentBook.titleEn
                        )}
                      </h4>

                      <div className="text-[11px] sm:text-xs text-gray-600 space-y-0.5">
                        <p>
                          <span className="font-semibold">
                            {t("book.author", "ผู้แต่ง")}:{" "}
                          </span>
                          {pickByLang(
                            langCode,
                            currentBook.authorTh,
                            currentBook.authorEn
                          )}
                        </p>
                        <p>
                          <span className="font-semibold">
                            {t("book.imprint", "พิมพ์ลักษณ์")}:{" "}
                          </span>
                          {pickByLang(
                            langCode,
                            currentBook.imprintTh,
                            currentBook.imprintEn
                          )}
                        </p>
                        <p>
                          <span className="font-semibold">
                            {t("book.physical", "จำนวนหน้า")}:{" "}
                          </span>
                          {currentBook.pages
                            ? `${currentBook.pages} ${t(
                                "book.pagesUnit",
                                "หน้า"
                              )}`
                            : "-"}
                        </p>
                        <p>
                          <span className="font-semibold">
                            {t("book.year", "ปีที่พิมพ์")}:{" "}
                          </span>
                          {currentBook.yearBe
                            ? `พ.ศ. ${currentBook.yearBe}`
                            : "-"}
                          {currentBook.yearCe
                            ? ` (${currentBook.yearCe})`
                            : ""}
                        </p>
                        <p>
                          <span className="font-semibold">
                            {t("book.age", "อายุหนังสือ")}:{" "}
                          </span>
                          {pickByLang(
                            langCode,
                            currentBook.ageTh,
                            currentBook.ageEn
                          )}
                        </p>
                        {currentBook.regNo && (
                          <p>
                            <span className="font-semibold">
                              {t("book.register", "เลขทะเบียน")}:{" "}
                            </span>
                            {currentBook.regNo}
                          </p>
                        )}
                      </div>

                      <p className="mt-2 text-[11px] sm:text-sm text-gray-800 leading-relaxed">
                        {pickByLang(
                          langCode,
                          currentBook.abstractTh,
                          currentBook.abstractEn
                        )}
                      </p>

                      {/* ปุ่มอ่านต่อ: ถ้ามี readerPath จะลิงก์ไปหน้าอ่าน */}
                      <div className="mt-3 flex justify-end">
                        {currentBook.readerPath ? (
                          <Link
                            to={currentBook.readerPath}
                            className="text-[11px] sm:text-xs px-3 py-1.5 rounded-full bg-[#d8653b] text-white hover:bg-[#c4552f] transition"
                          >
                            {t("templeExhibition.readMoreBtn", "อ่านต่อ")}
                          </Link>
                        ) : (
                          <button
                            type="button"
                            className="text-[11px] sm:text-xs px-3 py-1.5 rounded-full bg-gray-300 text-gray-600 cursor-default"
                          >
                            {t(
                              "templeExhibition.readMoreDisabled",
                              "ยังไม่เปิดหน้าอ่านในระบบ"
                            )}
                          </button>
                        )}
                      </div>
                    </article>
                  )}
                </div>
              )}

              {/* ถ้าสถานที่นี้ยังไม่มีหนังสือเชื่อมโยง */}
              {selectedTemple && books.length === 0 && (
                <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-sm p-5 text-xs sm:text-sm text-gray-700">
                  <p className="font-semibold text-[#5b4a3e] mb-1">
                    {selectedTemple.nameTh}
                  </p>
                  <p className="text-[11px] sm:text-xs text-gray-500 mb-2">
                    {selectedTemple.nameEn}
                  </p>
                  <p className="text-[11px] sm:text-xs text-gray-600">
                    {t(
                      "templeExhibition.noBooksYet",
                      "ยังไม่ได้เชื่อมหนังสือหายากในคลังเข้ากับสถานที่นี้ ข้อมูลจะถูกเพิ่มเติมในภายหลัง"
                    )}
                  </p>
                </div>
              )}
            </section>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}